import { ReactElement } from 'react'
import { Box, Button, Card } from '@mui/material'
import { NavigateNext, Cancel } from '@mui/icons-material'
import type { Course, LearningPath, TrainingSeries } from 'src/v3/core/types'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import CourseSelectionCard from '../../components/course-selection-card'
import SchedulingFieldsGroup from '../../components/scheduling-fields/scheduling-fields-group'
import type { ScheduledAssignmentFormData } from '../types'

type FormValues = Pick<
  ScheduledAssignmentFormData,
  | 'name'
  | 'linkedServiceID'
  | 'goLiveDate'
  | 'dueDate'
  | 'courseIDs'
  | 'hasRecurrence'
  | 'recurrence'
>

type FormErrors = Record<string, any>

type ScheduledAssignmentDetailsCourseStepProps = {
  values: FormValues
  errors: FormErrors
  courses: Course[]
  trainingSeries: TrainingSeries[]
  learningPaths?: LearningPath[]
  form: any
  onCancel: () => void
  onContinue: () => void
  canContinue: boolean
  isLoadingCourses?: boolean
}

export default function ScheduledAssignmentDetailsCoursesStep({
  courses,
  trainingSeries,
  learningPaths,
  form,
  onCancel,
  onContinue,
  canContinue,
  isLoadingCourses = false,
}: ScheduledAssignmentDetailsCourseStepProps): ReactElement {
  useScrollToTop()

  return (
    <>
      <Card className="p-6 mb-6">
        <SchedulingFieldsGroup form={form} />
      </Card>

      <form.Field
        name="courseIDs"
        mode="array"
        validators={{
          onChange: ({ value }: { value: string[] }) => {
            if (!value || value.length === 0) {
              return 'At least one course must be selected'
            }
            return undefined
          },
        }}
      >
        {(field: any) => (
          <CourseSelectionCard
            courses={courses}
            trainingSeries={trainingSeries}
            learningPaths={learningPaths}
            selectedCourseIDs={field.state.value}
            onSelectionChange={(ids) => field.handleChange(ids)}
            isLoadingCourses={isLoadingCourses}
            error={
              !field.state.meta.isValid
                ? field.state.meta.errors.join(', ')
                : undefined
            }
          />
        )}
      </form.Field>

      <Box className="flex justify-end gap-3">
        <Button
          variant="outlined"
          onClick={onCancel}
          size="large"
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={!canContinue}
          size="large"
          endIcon={<NavigateNext />}
        >
          Continue to User Selection
        </Button>
      </Box>
    </>
  )
}
